/**
 * PARÇA YÖNETİMİ — eski `koli_yonetimi.php` karşılığı.
 *
 * DÜZELTİLEN ESKİ HATALAR
 *  · Fiyat `bind_param("isi")` ile integer'a kırpılıyordu: "12,50" veritabanına
 *    12 yazılıyordu ve ondalık ayırıcı dönüşümü hiç yoktu. Burada fiyat KURUŞ
 *    (integer) olarak gider; Türkçe virgül `parseKurus` ile tek yerde çözülür.
 *  · `DELETE FROM erp_products` HARD DELETE'ti; reçete ve stok satırları yetim
 *    kalıyordu. Yeni modelde silme yok — parça `active:false` ile pasife alınır.
 *  · Doğrulama başarısız olduğunda ekran sessizce geri dönüyordu; kullanıcı
 *    kaydın gitmediğini anlamıyordu. Artık her hata olduğu gibi gösterilir.
 *  · Alış fiyatı artık parça dokümanında DEĞİL, `components/{id}/private/pricing`
 *    alt dokümanındadır (G-10). Yalnızca `costView` (boss + office) okur;
 *    `production` rolü parçayı görür, fiyatı GÖRMEZ, hiçbir şeyi DEĞİŞTİREMEZ.
 *
 * Bu dosya ürün/fiyat ekranlarının ortak para-giriş yardımcılarını da dışa verir;
 * para ayrıştırma tek bir yerde tanımlı olsun diye burada durur.
 */

import { useEffect, useMemo, useState } from "react";

import {
  collection,
  collectionGroup,
  doc,
  getCountFromServer,
  orderBy,
  query,
  where,
} from "firebase/firestore";

import { Modal } from "../components/Modal";
import { StatusBadge } from "../components/StatusBadge";
import { LoadingBlock } from "../components/Spinner";
import { EmptyState } from "../components/EmptyState";
import { useToast } from "../components/Toast";
import { useCollection } from "../hooks/useCollection";
import { useDoc } from "../hooks/useDoc";
import { useCallableAction } from "../hooks/useCallableAction";
import { db } from "../lib/firebase";
import { useCan } from "../lib/auth";
import { tl, n, date } from "../lib/format";
import { MoneyInput, DecimalInput, ErrorNote, Field, PageHeader } from "../components";
import type { ComponentDoc, ComponentCategory, ComponentPricing, UpsertComponentInput, UpsertComponentOutput } from "../lib/types";
import { parseDecimal, parseKurus, formatDecimalInput } from "../lib/decimal";

/**
 * Alış fiyatı hücresi — `costView`a bağlı AYRI sorgu.
 * Reçete/parça satırında fiyat denormalize EDİLMEZ (G-10); bu bileşen yalnızca
 * yetkili kullanıcı için render edilir, dolayısıyla yetkisiz kullanıcıda
 * abonelik hiç kurulmaz (Rules zaten reddederdi).
 */
export function PricingCell({ componentId }: { componentId: string }) {
  const { data, loading, error } = useDoc<ComponentPricing>(
    () => doc(db, "components", componentId, "private", "pricing"),
    [componentId],
  );

  if (loading) return <span className="text-xs text-gray-600">…</span>;
  if (error) return <span className="text-xs text-red-700">{error}</span>;
  if (!data) return <span className="text-xs text-amber-700">fiyat girilmemiş</span>;

  return (
    <span className="tabular-nums">
      {tl(data.unitPrice)}
      {data.currency && data.currency !== "TRY" ? (
        <span className="ml-1 text-xs text-gray-600">({data.currency})</span>
      ) : null}
    </span>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// EKRAN
// ═══════════════════════════════════════════════════════════════════════════

const PAGE_SIZES = [20, 50, 100, 200];

export function Components() {
  const can = useCan();
  const toast = useToast();

  // Silinmiş parça listelenmez; sıralama harf duyarsız `nameLower` üzerinden.
  const componentsQ = useCollection<ComponentDoc>(
    () => query(collection(db, "components"), where("deleted", "==", false), orderBy("nameLower")),
    [],
  );
  const categoriesQ = useCollection<ComponentCategory>(
    () =>
      query(collection(db, "componentCategories"), where("deleted", "==", false), orderBy("order")),
    [],
  );

  const [q, setQ] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [unit, setUnit] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [perPage, setPerPage] = useState(50);
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<ComponentDoc | "new" | null>(null);

  const components = componentsQ.docs;
  const categories = categoriesQ.docs;

  const units = useMemo(() => {
    const set = new Set<string>();
    for (const c of components) if (c.unit) set.add(c.unit);
    return Array.from(set).sort((a, b) => a.localeCompare(b, "tr"));
  }, [components]);

  const categoryName = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of categories) map.set(c.id, c.name);
    return map;
  }, [categories]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLocaleLowerCase("tr");
    return components.filter((c) => {
      if (!showInactive && c.active === false) return false;
      if (categoryId && c.categoryId !== categoryId) return false;
      if (unit && c.unit !== unit) return false;
      if (!needle) return true;
      // Eski ekran `productname` VEYA `externallabel` üzerinde LIKE yapıyordu.
      return (
        c.name.toLocaleLowerCase("tr").includes(needle) ||
        (c.externalLabel ?? "").toLocaleLowerCase("tr").includes(needle)
      );
    });
  }, [components, q, categoryId, unit, showInactive]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const safePage = Math.min(page, totalPages);
  const rows = filtered.slice((safePage - 1) * perPage, safePage * perPage);

  return (
    <div className="space-y-5">
      <PageHeader
        title="Parça Yönetimi"
        breadcrumb={[{ label: "Satın Alma", to: "/procurement" }, { label: "Parça Yönetimi" }]}
        description={
          <>
            Hammadde, aksesuar ve koli kataloğu. Silme yoktur — kullanımdan kaldırmak için parça
            <strong> pasife</strong> alınır; böylece reçete ve stok satırları yetim kalmaz.
            {can.isProduction ? " Üretim rolünde bu ekran salt okunurdur." : null}
          </>
        }
        actions={
          can.isStaff ? (
            <button type="button" className="btn-primary" onClick={() => setEditing("new")}>
              Yeni Parça Ekle
            </button>
          ) : null
        }
      />

      {/* ── Filtreler ─────────────────────────────────────────────────────── */}
      <div className="card card-pad grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-6">
        <div className="lg:col-span-2">
          <Field label="Ara (ad / dış etiket)" htmlFor="f-q">
            <input
              id="f-q"
              className="input"
              placeholder="Parça adı veya dış etiket"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(1);
              }}
            />
          </Field>
        </div>
        <Field label="Kategori" htmlFor="f-cat">
          <select
            id="f-cat"
            className="select"
            value={categoryId}
            onChange={(e) => {
              setCategoryId(e.target.value);
              setPage(1);
            }}
          >
            <option value="">Tüm Kategoriler</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Birim" htmlFor="f-unit">
          <select
            id="f-unit"
            className="select"
            value={unit}
            onChange={(e) => {
              setUnit(e.target.value);
              setPage(1);
            }}
          >
            <option value="">Tüm Birimler</option>
            {units.map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Sayfa boyutu" htmlFor="f-per">
          <select
            id="f-per"
            className="select"
            value={perPage}
            onChange={(e) => {
              setPerPage(Number(e.target.value));
              setPage(1);
            }}
          >
            {PAGE_SIZES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </Field>
        <label className="flex items-end gap-2 pb-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => {
              setShowInactive(e.target.checked);
              setPage(1);
            }}
          />
          Pasifleri göster
        </label>
      </div>

      <ErrorNote error={componentsQ.error} />
      {componentsQ.loading ? <LoadingBlock /> : null}

      {!componentsQ.loading && !componentsQ.error && filtered.length === 0 ? (
        <EmptyState
          title="Parça bulunamadı"
          description="Filtreleri değiştirin veya yeni bir parça ekleyin."
        />
      ) : null}

      {filtered.length > 0 ? (
        <>
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>Toplam: {n(filtered.length)} parça</span>
            <span>
              Sayfa {safePage} / {totalPages}
            </span>
          </div>

          <div className="overflow-x-auto rounded-lg bg-white ring-1 ring-gray-200">
            <table className="table-dense">
              <caption className="sr-only">Parça kataloğu</caption>
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col">Parça</th>
                  <th scope="col" className="hidden sm:table-cell">
                    Kategori
                  </th>
                  {/* ★ TELEFON: birim ve durum telefonda gizlenip parça adının
                      altına iner (tablo 451px yer istiyordu, kap 343px). */}
                  <th scope="col" className="hidden sm:table-cell">
                    Birim
                  </th>
                  <th scope="col" className="hidden sm:table-cell text-right">
                    Y×G×U (cm)
                  </th>
                  {/* "Desi" sütunu kaldırıldı — gerekçe gövdedeki yorumda. */}
                  {/* Alış fiyatı yalnızca costView (boss + office). */}
                  {can.seeCost ? (
                    <th scope="col" className="text-right">
                      Alış Fiyatı
                    </th>
                  ) : null}
                  <th scope="col" className="hidden sm:table-cell">
                    Durum
                  </th>
                  <th scope="col" className="text-right">
                    İşlem
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {rows.map((c) => {
                  const d = c.dimensions ?? {};
                  return (
                    <tr key={c.id}>
                      <td>
                        <span className="font-medium text-gray-900">{c.name}</span>
                        {c.isPackaging ? (
                          <StatusBadge label="Koli" color="purple" className="ml-2" />
                        ) : null}
                        {c.externalLabel ? (
                          <span className="block text-xs text-gray-600">{c.externalLabel}</span>
                        ) : null}
                        {/* Telefonda gizlenen birim ve durum burada görünür. */}
                        <span className="mt-1 flex items-center gap-2 text-xs text-gray-600 sm:hidden">
                          {c.unit}
                          <StatusBadge
                            label={c.active === false ? "Pasif" : "Aktif"}
                            color={c.active === false ? "gray" : "green"}
                          />
                        </span>
                      </td>
                      <td className="hidden sm:table-cell">
                        {c.categoryName ?? categoryName.get(c.categoryId) ?? "—"}
                      </td>
                      <td className="hidden sm:table-cell">{c.unit}</td>
                      <td className="hidden sm:table-cell text-right tabular-nums">
                        {d.height || d.width || d.length
                          ? `${n(d.height ?? 0)}×${n(d.width ?? 0)}×${n(d.length ?? 0)}`
                          : "—"}
                      </td>
                      {/*
                        ★ ÖLÇÜLEN GERÇEK: `components/{id}.deci` DİYE BİR ALAN YAZILMIYOR.
                        Eski yorum "Desi sunucudan gelir" diyordu; canlıda 312 parçanın
                        312'sinde bu alan YOK, dolayısıyla sütun istisnasız "—" idi.
                        Desi tek yerde, sunucudaki `previewDesi` ucuyla hesaplanır ve
                        koli ekranında gösterilir — 312 satırlık listede satır başına bir
                        çağrı yapmak da doğru olmazdı. Sütun kaldırıldı: yanındaki "Ölçü"
                        sütunu zaten ham veriyi gösteriyor, boş bir sütun ise "bu parçanın
                        desisi yok" diye okunuyordu.
                      */}
                      {can.seeCost ? (
                        <td className="text-right">
                          <PricingCell componentId={c.id} />
                        </td>
                      ) : null}
                      <td className="hidden sm:table-cell">
                        <StatusBadge
                          label={c.active === false ? "Pasif" : "Aktif"}
                          color={c.active === false ? "gray" : "green"}
                        />
                      </td>
                      <td className="text-right">
                        <button
                          type="button"
                          className="btn-secondary"
                          onClick={() => setEditing(c)}
                        >
                          {can.isStaff ? "Düzenle" : "Görüntüle"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between">
            <button
              type="button"
              className="btn-secondary"
              disabled={safePage <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              ← Önceki
            </button>
            <button
              type="button"
              className="btn-secondary"
              disabled={safePage >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Sonraki →
            </button>
          </div>
        </>
      ) : null}

      {editing ? (
        <ComponentEditor
          value={editing === "new" ? null : editing}
          categories={categories}
          onClose={() => setEditing(null)}
          onSaved={(name) => {
            toast.success(`"${name}" kaydedildi.`);
            setEditing(null);
          }}
        />
      ) : null}
    </div>
  );
}

export default Components;

// ═══════════════════════════════════════════════════════════════════════════
// PARÇA DÜZENLEME
// `production` rolünde tüm alanlar readOnly ve kaydet butonu gizlidir (G-11).
// ═══════════════════════════════════════════════════════════════════════════

function ComponentEditor({
  value,
  categories,
  onClose,
  onSaved,
}: {
  value: ComponentDoc | null;
  categories: ComponentCategory[];
  onClose: () => void;
  onSaved: (name: string) => void;
}) {
  const can = useCan();
  const readOnly = !can.isStaff;
  const save = useCallableAction<UpsertComponentInput, UpsertComponentOutput>("upsertComponent");

  const [name, setName] = useState(value?.name ?? "");
  const [categoryId, setCategoryId] = useState(value?.categoryId ?? "");
  const [unit, setUnit] = useState(value?.unit ?? "adet");
  const [height, setHeight] = useState(formatDecimalInput(value?.dimensions?.height));
  const [width, setWidth] = useState(formatDecimalInput(value?.dimensions?.width));
  const [length, setLength] = useState(formatDecimalInput(value?.dimensions?.length));
  const [active, setActive] = useState(value?.active !== false);
  const [isPackaging, setIsPackaging] = useState(value?.isPackaging === true);
  const [selectable, setSelectable] = useState(value?.isSelectableAsComponent !== false);
  const [localError, setLocalError] = useState<string | null>(null);

  // Fiyat yalnızca costView'da gösterilir ve gönderilir.
  const [priceText, setPriceText] = useState("");
  const [supplier, setSupplier] = useState("");
  const [currency, setCurrency] = useState<"TRY" | "EUR" | "USD">("TRY");
  const [priceTouched, setPriceTouched] = useState(false);

  const selectedCategory = categories.find((c) => c.id === categoryId);

  async function submit() {
    setLocalError(null);

    if (!name.trim()) return setLocalError("Parça adı zorunludur.");
    if (!categoryId) return setLocalError("Kategori seçilmelidir.");
    if (!unit.trim()) return setLocalError("Birim zorunludur (ör. adet, m2).");

    const dims = {
      height: parseDecimal(height) ?? 0,
      width: parseDecimal(width) ?? 0,
      length: parseDecimal(length) ?? 0,
    };

    // Koli desisiz olamaz: eksik ölçü eski sistemde sessizce 0 desi üretiyordu
    // (Yase190x90 kolisi) ve kargo maliyeti sıfır çıkıyordu.
    if (isPackaging && (dims.height <= 0 || dims.width <= 0 || dims.length <= 0)) {
      return setLocalError(
        "Koli parçasında yükseklik, genişlik ve uzunluk sıfırdan büyük olmalıdır (cm).",
      );
    }

    const input: UpsertComponentInput = {
      ...(value ? { componentId: value.id } : {}),
      name: name.trim(),
      categoryId,
      unit: unit.trim(),
      dimensions: dims,
      active,
      isPackaging,
      isSelectableAsComponent: selectable,
    };

    if (can.seeCost && priceTouched) {
      // Yerel ayrıştırma YALNIZCA doğrulama içindir; sunucuya HAM METİN gider.
      // Kuruşa çevirip göndermek çift dönüşüm demek olurdu (450 ₺ → 45.000 ₺).
      const kurus = parseKurus(priceText);
      if (kurus === null) {
        return setLocalError("Alış fiyatı okunamadı. Ondalık için virgül kullanın (örnek: 12,50).");
      }
      if (kurus < 0) return setLocalError("Alış fiyatı negatif olamaz.");
      input.unitPrice = priceText;
      input.currency = currency;
      input.supplier = supplier.trim();
    }

    const res = await save.run(input);
    if (res) onSaved(input.name);
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={value ? `Parça: ${value.name}` : "Yeni Parça"}
      description={
        readOnly ? "Üretim rolünde parça bilgileri yalnızca görüntülenebilir." : undefined
      }
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Kapat
          </button>
          {!readOnly ? (
            <button type="button" className="btn-primary" onClick={submit} disabled={save.pending}>
              {save.pending ? "Kaydediliyor…" : "Kaydet"}
            </button>
          ) : null}
        </>
      }
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Field label="Parça Adı" htmlFor="c-name">
            <input
              id="c-name"
              className="input"
              value={name}
              readOnly={readOnly}
              maxLength={300}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
        </div>

        <Field label="Kategori" htmlFor="c-cat">
          <select
            id="c-cat"
            className="select"
            value={categoryId}
            disabled={readOnly}
            onChange={(e) => {
              const id = e.target.value;
              setCategoryId(id);
              // Kategori ambalajsa koli bayrağı önerilir; kullanıcı ezebilir.
              const cat = categories.find((c) => c.id === id);
              if (cat) setIsPackaging(cat.isPackaging);
            }}
          >
            <option value="">Seçiniz…</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Birim" hint="Gözlenen değerler: adet, m2" htmlFor="c-unit">
          <input
            id="c-unit"
            className="input"
            value={unit}
            readOnly={readOnly}
            onChange={(e) => setUnit(e.target.value)}
          />
        </Field>

        {/* ★ TELEFON: 3 ölçü alanı 375px'te yan yana sıkışıyordu; dar ekranda
            tek sütuna iner, tabletten itibaren eski 3'lü düzen korunur. */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:col-span-2">
          <Field label="Yükseklik (cm)" htmlFor="c-h">
            <DecimalInput id="c-h" value={height} onChange={setHeight} readOnly={readOnly} />
          </Field>
          <Field label="Genişlik (cm)" htmlFor="c-w">
            <DecimalInput id="c-w" value={width} onChange={setWidth} readOnly={readOnly} />
          </Field>
          <Field label="Uzunluk (cm)" htmlFor="c-l">
            <DecimalInput id="c-l" value={length} onChange={setLength} readOnly={readOnly} />
          </Field>
        </div>

        <div className="flex flex-wrap gap-4 rounded-md bg-gray-50 p-3 text-sm sm:col-span-2">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={isPackaging}
              disabled={readOnly}
              onChange={(e) => setIsPackaging(e.target.checked)}
            />
            Koli / ambalaj parçası
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={selectable}
              disabled={readOnly}
              onChange={(e) => setSelectable(e.target.checked)}
            />
            Reçetede bileşen olarak seçilebilir
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={active}
              disabled={readOnly}
              onChange={(e) => setActive(e.target.checked)}
            />
            Aktif
          </label>
        </div>

        {selectedCategory && !selectedCategory.selectableInRecipe ? (
          <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 sm:col-span-2">
            Bu kategori reçete seçicisinde gösterilmez. Parça yine de kayıtlıdır.
          </p>
        ) : null}

        {/* ── Alış fiyatı: costView'a bağlı ayrı blok ─────────────────────── */}
        {can.seeCost ? (
          <div className="rounded-lg border border-green-300 bg-green-50/60 p-3 sm:col-span-2">
            <h3 className="text-sm font-semibold text-green-900">
              Alış Fiyatı{" "}
              <span className="font-normal text-green-800">(yalnızca yetkili roller)</span>
            </h3>
            {value ? (
              <p className="mt-1 text-xs text-green-900">
                Mevcut fiyat: <PricingCell componentId={value.id} />
              </p>
            ) : null}
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <Field label="Yeni Alış Fiyatı" hint="Ondalık için virgül: 12,50" htmlFor="c-price">
                <MoneyInput
                  id="c-price"
                  value={priceText}
                  readOnly={readOnly}
                  onChange={(t) => {
                    setPriceText(t);
                    setPriceTouched(true);
                  }}
                />
              </Field>
              <Field label="Para Birimi" htmlFor="c-cur">
                <select
                  id="c-cur"
                  className="select"
                  value={currency}
                  disabled={readOnly}
                  onChange={(e) => {
                    setCurrency(e.target.value as "TRY" | "EUR" | "USD");
                    setPriceTouched(true);
                  }}
                >
                  <option value="TRY">TRY</option>
                  <option value="EUR">EUR</option>
                  <option value="USD">USD</option>
                </select>
              </Field>
              <Field label="Tedarikçi" htmlFor="c-sup">
                <input
                  id="c-sup"
                  className="input"
                  value={supplier}
                  readOnly={readOnly}
                  onChange={(e) => {
                    setSupplier(e.target.value);
                    setPriceTouched(true);
                  }}
                />
              </Field>
            </div>
            <p className="mt-2 text-xs text-green-900">
              Fiyat boş bırakılırsa değiştirilmez — yalnızca diğer alanlar güncellenir.
            </p>
            {value ? (
              <p className="mt-1 text-xs text-green-800">
                Son fiyat güncellemesi: <PricingUpdatedAt componentId={value.id} />
              </p>
            ) : null}
          </div>
        ) : null}
      </div>

      {/*
        ★ KULLANIM SAYISI ARTIK CANLI SAYILIYOR — SAYAÇ ALANINDAN OKUNMUYOR.

        Burası `value.usedInRecipeCount` okuyordu. O alan bir SAYAÇ ve canlıda
        312 parçanın 312'sinde 0: hiçbir kod onu artırmıyor (`functions/` altında
        adı yalnızca `core/model.ts` tip tanımında geçiyor). Değer 0 olduğu için
        `? :` koşulu hiç doğru olmuyor ve bu uyarı HİÇ GÖRÜNMÜYORDU — oysa
        gerçek kullanım 98 parçaya ve 5.766 reçete satırına yayılmış durumda.
        Yani parçayı pasife alan kullanıcıya "bu parça N reçetede kullanılıyor"
        uyarısı bir kez bile çıkmadı.

        Sayacı tetikleyiciyle canlı tutmak da mümkündü ama sayaç ile gerçek
        arasında kalıcı SAPMA ihtimali bırakır (bu projede birden çok kez oldu).
        `getCountFromServer` sorgunun kendisidir: sunucuda sayar, dokümanları
        indirmez ve sapamaz.
      */}
      <RecipeUsageNote componentId={value?.id ?? ""} />

      <ErrorNote error={localError ?? save.error} />
    </Modal>
  );
}

/**
 * Parçanın reçete + koli satırlarındaki kullanımını CANLI sayar.
 *
 * Ayak (`legs`) satırları sayılmaz: onlar bileşene değil serbest metne dayanır
 * (`firestore.rules` §legs: canlıda 774 ayak satırının 774'ünde `componentId`
 * alanı hiç yok).
 *
 * Sayım başarısızsa hiçbir şey gösterilmez — "sayılamadı" ile "kullanılmıyor"
 * aynı şey değildir ve burada yanlış tarafta hata yapmak kullanıcıya var olmayan
 * bir güvence vermek olurdu.
 */
function RecipeUsageNote({ componentId }: { componentId: string }) {
  const [count, setCount] = useState<number | null>(null);

  useEffect(() => {
    if (!componentId) {
      setCount(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const snaps = await Promise.all(
          (["recipes", "packaging"] as const).map((g) =>
            getCountFromServer(
              query(collectionGroup(db, g), where("componentId", "==", componentId)),
            ),
          ),
        );
        const total = snaps.reduce((sum, s) => sum + s.data().count, 0);
        if (!cancelled) setCount(total);
      } catch {
        // Sayılamadı → sessiz kal (yanlış güvence verme).
        if (!cancelled) setCount(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [componentId]);

  if (count === null || count === 0) return null;

  return (
    <p className="mt-3 text-xs text-gray-500">
      Bu parça {n(count)} reçete/koli satırında kullanılıyor. Pasife alırsanız reçeteler bozulmaz,
      ancak maliyet hesabı "eksik veri" uyarısı üretir.
    </p>
  );
}

function PricingUpdatedAt({ componentId }: { componentId: string }) {
  const { data } = useDoc<ComponentPricing>(
    () => doc(db, "components", componentId, "private", "pricing"),
    [componentId],
  );
  if (!data) return <span>—</span>;
  return <span>{date(data.lastPurchaseAt ?? data.updatedAt ?? null)}</span>;
}
